import React, { useEffect, useState, useRef, useCallback } from 'react';
import { database } from '../Firebase';
import {
  collection, addDoc, onSnapshot, query, doc,
  updateDoc, increment, deleteDoc, getDocs, where, getDoc
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import Button from 'react-bootstrap/Button';
import "../styles/css/Maps.css";
import waze from "../styles/images/waze.png";
import google_maps from "../styles/images/google-maps.png";
import { LuMilestone } from 'react-icons/lu';

const DAILY_LIMIT    = 10;
const MIN_DELAY_MS   = 5 * 60 * 1000;
const MIN_DISTANCE_M = 100;

const HOPITAL = {
  lat:   48.8313,
  lng:   2.1272,
  nom:   "Hôpital André Mignot",
  ville: "Le Chesnay-Rocquencourt",
  rayon: 300,
};

const geocodeCache = {};

const pinSvg = (color) => `
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g>
      <path d="M16 2C9.37258 2 4 7.37258 4 14C4 23.5 16 30 16 30C16 30 28 23.5 28 14C28 7.37258 22.6274 2 16 2Z"
        fill="${color}" stroke="#333" stroke-width="2"/>
      <circle cx="16" cy="14" r="5" fill="white" stroke="#333" stroke-width="2"/>
    </g>
  </svg>`;

const getSpotStatus = (timestamp) => {
  const ageMin = (Date.now() - timestamp) / 60000;
  if (ageMin < 5)  return { color: '#27ae60', label: 'Récent',  expired: false };
  if (ageMin < 10) return { color: '#f39c12', label: 'Moyen',   expired: false };
  if (ageMin < 15) return { color: '#e74c3c', label: 'Ancien',  expired: false };
  return { color: null, label: null, expired: true };
};

const distanceApproximative = (d) => {
  if (d < 100) return "< 100m";
  if (d < 200) return "< 200m";
  if (d < 300) return "< 300m";
  if (d < 400) return "< 400m";
  return "< 500m";
};

const calculateDistanceMeters = (lat1, lon1, lat2, lon2) => {
  const R    = 6371000;
  const dLat = (lat2-lat1) * Math.PI/180;
  const dLon = (lon2-lon1) * Math.PI/180;
  const a    = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
  return R * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)) * 2;
};

const estDansZone = (lat, lng) =>
  calculateDistanceMeters(lat, lng, HOPITAL.lat, HOPITAL.lng) <= HOPITAL.rayon;

// ── Geocoding restreint à la zone hôpital (pour adresse départ) ───────────
const geocodeWithCache = async (address) => {
  if (!address) throw new Error("Adresse vide");
  if (geocodeCache[address]) return geocodeCache[address];
  const apiKey = process.env.REACT_APP_HERE_API_KEY;
  if (!apiKey) throw new Error("Clé HERE Maps manquante");
  try {
    const r1 = await fetch(
      `https://geocode.search.hereapi.com/v1/geocode` +
      `?q=${encodeURIComponent(address)}` +
      `&in=circle:${HOPITAL.lat},${HOPITAL.lng};r=${HOPITAL.rayon * 3}` +
      `&apiKey=${apiKey}`
    );
    const d1 = await r1.json();
    if (d1.items?.length > 0) {
      const pos = d1.items[0].position;
      geocodeCache[address] = pos;
      return pos;
    }
  } catch {}
  try {
    const r2 = await fetch(
      `https://discover.search.hereapi.com/v1/discover` +
      `?q=${encodeURIComponent(address)}&lang=fr&limit=1` +
      `&in=circle:${HOPITAL.lat},${HOPITAL.lng};r=${HOPITAL.rayon * 3}` +
      `&apiKey=${apiKey}`
    );
    const d2 = await r2.json();
    if (d2.items?.length > 0) {
      const pos = d2.items[0].position;
      geocodeCache[address] = pos;
      return pos;
    }
  } catch {}
  throw new Error("Lieu introuvable dans la zone de l'hôpital");
};

// ── Geocoding libre — France entière (pour destination itinéraire) ────────
const geocodeLibre = async (address) => {
  if (!address) throw new Error("Adresse vide");
  const cacheKey = `libre_${address}`;
  if (geocodeCache[cacheKey]) return geocodeCache[cacheKey];
  const apiKey = process.env.REACT_APP_HERE_API_KEY;
  if (!apiKey) throw new Error("Clé HERE Maps manquante");
  try {
    const r = await fetch(
      `https://geocode.search.hereapi.com/v1/geocode` +
      `?q=${encodeURIComponent(address)}&lang=fr&limit=1` +
      `&in=countryCode:FRA` +
      `&apiKey=${apiKey}`
    );
    const d = await r.json();
    if (d.items?.length > 0) {
      const pos = d.items[0].position;
      geocodeCache[cacheKey] = pos;
      return pos;
    }
  } catch {}
  try {
    const r2 = await fetch(
      `https://discover.search.hereapi.com/v1/discover` +
      `?q=${encodeURIComponent(address)}&lang=fr&limit=1` +
      `&apiKey=${apiKey}`
    );
    const d2 = await r2.json();
    if (d2.items?.length > 0) {
      const pos = d2.items[0].position;
      geocodeCache[cacheKey] = pos;
      return pos;
    }
  } catch {}
  throw new Error("Adresse introuvable");
};

const formatDuration = (s) => {
  if (!s) return '';
  const min = Math.floor(s/60);
  return `${min} min ${s%60 < 10 ? '0' : ''}${s%60} s`;
};

// ── localStorage helpers ──────────────────────────────────────────────────
const STORAGE_KEY = (userId) => `spotypark_signalements_${userId}`;

const getCompteurLocal = (userId) => {
  if (!userId) return { count: 0, date: null, dernierSignalement: null };
  try {
    const raw = localStorage.getItem(STORAGE_KEY(userId));
    if (!raw) return { count: 0, date: null, dernierSignalement: null };
    const data    = JSON.parse(raw);
    const aujourd = new Date().toDateString();
    if (data.date !== aujourd) return { count: 0, date: aujourd, dernierSignalement: null };
    return data;
  } catch { return { count: 0, date: null, dernierSignalement: null }; }
};

const setCompteurLocal = (userId, count, dernierSignalement) => {
  if (!userId) return;
  try {
    localStorage.setItem(STORAGE_KEY(userId), JSON.stringify({
      count,
      date: new Date().toDateString(),
      dernierSignalement,
    }));
  } catch {}
};

// ══════════════════════════════════════════════════════════════════════════════
// ADDRESS INPUT — version avec mode "libre" (France entière)
// ══════════════════════════════════════════════════════════════════════════════
const AddressInput = ({
  value, onChange, placeholder,
  onGeolocate, showGeolocateBtn = false,
  modeLibre = false  // ← nouveau : si true, pas de restriction zone
}) => {
  const [suggestions,     setSuggestions]    = useState([]);
  const [showSuggestions, setShowSuggestions]= useState(false);
  const [loadingSug,      setLoadingSug]     = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef  = useRef(null);
  const skipBlurRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    const trimmed = q?.trim() || '';
    if (trimmed.length < 3) { setSuggestions([]); setShowSuggestions(false); return; }
    const apiKey = process.env.REACT_APP_HERE_API_KEY;
    if (!apiKey) return;
    const cacheKey = `${modeLibre ? 'libre' : 'zone'}_ac_${trimmed.toLowerCase()}`;
    if (geocodeCache[cacheKey]) {
      setSuggestions(geocodeCache[cacheKey]);
      setShowSuggestions(geocodeCache[cacheKey].length > 0);
      return;
    }
    setLoadingSug(true);

    // Paramètre de restriction géographique
    const geoParam = modeLibre
      ? `&in=countryCode:FRA`
      : `&in=circle:${HOPITAL.lat},${HOPITAL.lng};r=${HOPITAL.rayon * 3}`;

    let itemsAuto = [];
    try {
      const r = await fetch(
        `https://autocomplete.search.hereapi.com/v1/autocomplete` +
        `?q=${encodeURIComponent(trimmed)}&lang=fr&limit=5` +
        `${geoParam}` +
        `&apiKey=${apiKey}`
      );
      if (r.ok) {
        const d = await r.json();
        itemsAuto = (d.items || []).map(item => ({
          id: item.id, title: item.address?.label || item.title || '',
          address: item.address || {}, position: null, _fromDiscover: false,
        }));
      }
    } catch (e) { console.warn("autocomplete:", e); }

    let itemsDiscover = [];
    try {
      const r = await fetch(
        `https://discover.search.hereapi.com/v1/discover` +
        `?q=${encodeURIComponent(trimmed)}&lang=fr&limit=4` +
        `${geoParam}` +
        `&apiKey=${apiKey}`
      );
      if (r.ok) {
        const d = await r.json();
        itemsDiscover = (d.items || []).map(item => ({
          id: item.id, title: item.address?.label || item.title || '',
          address: { label: item.address?.label || item.title || '', city: item.address?.city || '', postalCode: item.address?.postalCode || '' },
          position: item.position || null, _fromDiscover: true,
        }));
      }
    } catch (e) { console.warn("discover:", e); }

    const seen  = new Set();
    const items = [...itemsDiscover, ...itemsAuto].filter(item => {
      const key = (item.title || '').toLowerCase().trim();
      if (!key || seen.has(key)) return false;
      seen.add(key); return true;
    }).slice(0, 7);
    geocodeCache[cacheKey] = items;
    setSuggestions(items);
    setShowSuggestions(items.length > 0);
    setLoadingSug(false);
  }, [modeLibre]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val, null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 350);
  };

  const handleSelectSuggestion = async (item) => {
    const label = item.address?.label || item.title || '';
    setSuggestions([]); setShowSuggestions(false); onChange(label, null);
    if (item._fromDiscover && item.position) { onChange(label, item.position); return; }
    const apiKey = process.env.REACT_APP_HERE_API_KEY;
    if (!apiKey || !item.id) return;
    const cKey = `lk_${item.id}`;
    if (geocodeCache[cKey]) { onChange(label, geocodeCache[cKey]); return; }
    try {
      const r    = await fetch(`https://lookup.search.hereapi.com/v1/lookup?id=${item.id}&apiKey=${apiKey}`);
      const data = await r.json();
      if (data.position) { geocodeCache[cKey] = data.position; onChange(label, data.position); }
    } catch (e) { console.warn("lookup:", e); }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          type="text" className="form-control"
          placeholder={placeholder} value={value}
          onChange={handleInputChange}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          onBlur={() => {
            if (skipBlurRef.current) { skipBlurRef.current = false; return; }
            setTimeout(() => setShowSuggestions(false), 100);
          }}
          autoComplete="off"
          style={{ fontSize: '16px', paddingRight: showGeolocateBtn ? '44px' : '12px' }}
        />
        {loadingSug && (
          <div style={{ position: 'absolute', right: showGeolocateBtn ? '48px' : '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.75rem', color: '#6c757d', pointerEvents: 'none' }}>⏳</div>
        )}
        {showGeolocateBtn && (
          <button type="button" onClick={onGeolocate}
            style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '0', lineHeight: 1, color: '#3498db', minWidth: '36px', minHeight: '36px' }}>
            📍
          </button>
        )}
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 99999, background: '#ffffff', border: '1px solid #ced4da', borderTop: 'none', borderRadius: '0 0 8px 8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: '300px', overflowY: 'auto' }}>
          {suggestions.map((item, index) => (
            <div key={item.id || index}
              onMouseDown={() => { skipBlurRef.current = true; }}
              onClick={() => handleSelectSuggestion(item)}
              style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: index < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none', background: '#ffffff', userSelect: 'none' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'}
              onMouseLeave={e => e.currentTarget.style.background = '#ffffff'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ flexShrink: 0 }}>{item._fromDiscover ? '📍' : '🛣️'}</span>
                <span style={{ fontSize: '0.88rem', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#212529' }}>
                  {item.address?.label || item.title}
                </span>
                {item._fromDiscover && (
                  <span style={{ fontSize: '0.6rem', background: '#dbeafe', color: '#1d4ed8', padding: '2px 6px', borderRadius: '8px', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>POI</span>
                )}
              </div>
              {item.address?.city && (
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '2px', paddingLeft: '28px' }}>
                  {item.address.city}{item.address.postalCode ? ` — ${item.address.postalCode}` : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════
const HereMapComponent = () => {
  const [map,                    setMap]                    = useState(null);
  const [userMarker,             setUserMarker]             = useState(null);
  const [userAddress,            setUserAddress]            = useState('');
  const [availableSpots,         setAvailableSpots]         = useState([]);
  const [userPosition,           setUserPosition]           = useState(null);
  const [routeLine,              setRouteLine]              = useState(null);
  const [start,                  setStart]                  = useState('');
  const [startCoords,            setStartCoords]            = useState(null);
  const [end,                    setEnd]                    = useState('');
  const [endCoords,              setEndCoords]              = useState(null);
  const [routeDuration,          setRouteDuration]          = useState(null);
  const [routeMarkers,           setRouteMarkers]           = useState({ start: null, end: null });
  const [isAuthenticated,        setIsAuthenticated]        = useState(false);
  const [signalementsAujourdhui, setSignalementsAujourdhui] = useState(0);
  const [dernierSignalement,     setDernierSignalement]     = useState(null);
  const [signalementMessage,     setSignalementMessage]     = useState("");
  const [zoneMessage,            setZoneMessage]            = useState("");
  const [hasRoute,               setHasRoute]               = useState(false);

  // ── Navigation externe ────────────────────────────────────────────────────
  const [showNavModal,      setShowNavModal]      = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showRaisonModal,   setShowRaisonModal]   = useState(false);
  const [placeSelectionnee, setPlaceSelectionnee] = useState(null);
  const [spotIdSelectionne, setSpotIdSelectionne] = useState(null);
  const [spotSignaleurId,   setSpotSignaleurId]   = useState(null);
  const [placeEstBorne,     setPlaceEstBorne]     = useState(false);
  const feedbackTimerRef = useRef(null);
  const borneTimerRef    = useRef(null);

  // ── Type de place ─────────────────────────────────────────────────────────
  const [showTypeModal,        setShowTypeModal]        = useState(false);
  const [coordsEnAttente,      setCoordsEnAttente]      = useState(null);
  const [typePlace,            setTypePlace]            = useState('');
  const [niveauPlace,          setNiveauPlace]          = useState('');
  const [referenceEmplacement, setReferenceEmplacement] = useState('');

  const mapContainerRef   = useRef(null);
  const mapInitialized    = useRef(false);
  const spotsMarkersGroup = useRef(null);
  const uiRef             = useRef(null);
  const mapRef            = useRef(null);
  const routeLineRef      = useRef(null);
  const routeMarkersRef   = useRef({ start: null, end: null });
  const userMarkerRef     = useRef(null);
  const userPositionRef   = useRef(null);

  useEffect(() => { mapRef.current          = map;          }, [map]);
  useEffect(() => { routeLineRef.current    = routeLine;    }, [routeLine]);
  useEffect(() => { routeMarkersRef.current = routeMarkers; }, [routeMarkers]);
  useEffect(() => { userMarkerRef.current   = userMarker;   }, [userMarker]);
  useEffect(() => { userPositionRef.current = userPosition; }, [userPosition]);

  const createPinMarker = (color) =>
    new window.H.map.Icon(
      `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(pinSvg(color))}`,
      { size: { w: 32, h: 32 }, anchor: { x: 16, y: 32 } }
    );

  // ── Helpers notifications ─────────────────────────────────────────────────
  const demanderPermissionNotif = async () => {
    if (!('Notification' in window)) return false;
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  };

  const envoyerNotifFeedback = useCallback(() => {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      setShowFeedbackModal(true);
      return;
    }
    try {
      const notif = new Notification('SpotyPark 🅿️', {
        body: 'Avez-vous trouvé votre place ?',
        icon: '/favicon.ico',
        requireInteraction: true,
      });
      notif.onclick = () => { window.focus(); notif.close(); setShowFeedbackModal(true); };
    } catch { setShowFeedbackModal(true); }
  }, []);

  const envoyerNotifBorne = useCallback(() => {
    const message = 'Votre véhicule est en charge. Veuillez libérer la borne afin d\'en laisser le libre accès.';
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      alert(`⚡ ${message}`);
      return;
    }
    try {
      const notif = new Notification('SpotyPark ⚡', {
        body: message,
        icon: '/favicon.ico',
        requireInteraction: true,
      });
      notif.onclick = () => { window.focus(); notif.close(); };
    } catch { alert(`⚡ ${message}`); }
  }, []);

  // ── Calcule timer selon distance ──────────────────────────────────────────
  const calculerTimer = (distanceMetres, estBorne) => {
    const tempsTrajetMs = (distanceMetres / (20000 / 3600)) * 1000;
    if (estBorne) return tempsTrajetMs + 5 * 60 * 1000 + 4 * 60 * 60 * 1000;
    return Math.max(tempsTrajetMs + 2 * 60 * 1000, 3 * 60 * 1000);
  };

  // ── Lance navigation externe ──────────────────────────────────────────────
  // avecFeedback = false si destination hors zone (itinéraire libre)
  const lancerNavigationExterne = useCallback(async (spot, userPos, estBorne, avecFeedback = true) => {
    await demanderPermissionNotif();
    const dist  = userPos
      ? calculateDistanceMeters(userPos.lat, userPos.lng, spot.lat, spot.lng)
      : 300;
    const timer = calculerTimer(dist, estBorne);
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    // Lance le timer feedback UNIQUEMENT si destination dans zone Mignot
    if (avecFeedback) {
      feedbackTimerRef.current = setTimeout(() => {
        if (estBorne) envoyerNotifBorne();
        else envoyerNotifFeedback();
      }, timer);
    }
    setShowNavModal(false);
  }, [envoyerNotifFeedback, envoyerNotifBorne]);

  const ouvrirWaze = useCallback((spot, userPos, estBorne = false, avecFeedback = true) => {
    lancerNavigationExterne(spot, userPos, estBorne, avecFeedback);
    window.open(`https://waze.com/ul?ll=${spot.lat},${spot.lng}&navigate=yes`, '_blank');
  }, [lancerNavigationExterne]);

  const ouvrirGoogleMaps = useCallback((spot, userPos, estBorne = false, avecFeedback = true) => {
    lancerNavigationExterne(spot, userPos, estBorne, avecFeedback);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${spot.lat},${spot.lng}&travelmode=driving`, '_blank');
  }, [lancerNavigationExterne]);

  // ── Navigation depuis formulaire (destination libre ou zone) ─────────────
  const handleNaviguerFormulaire = useCallback((ouvrirFn) => {
    if (!endCoords) return;
    // Détecte si la destination est dans la zone Mignot
    const avecFeedback = estDansZone(endCoords.lat, endCoords.lng);
    ouvrirFn(
      { lat: endCoords.lat, lng: endCoords.lng },
      userPositionRef.current,
      false,       // estBorne (formulaire libre = jamais une borne)
      avecFeedback // feedback seulement si destination = parking Mignot
    );
  }, [endCoords]);

  // ── Traiter avertissement ─────────────────────────────────────────────────
  const traiterAvertissement = useCallback(async (signaleurId, spotId, raison) => {
    if (!signaleurId) return;
    try {
      const avertSnap = await getDocs(query(
        collection(database, 'avertissements'),
        where('signaleurId', '==', signaleurId)
      ));
      await addDoc(collection(database, 'avertissements'), {
        signaleurId,
        spotId:     spotId || null,
        raison,
        timestamp:  Date.now(),
        reportedBy: getAuth().currentUser?.uid || 'anonymous',
      });
      const nbAvertissements = avertSnap.size + 1;
      if (nbAvertissements >= 2) {
        const suspendJusqu = Date.now() + 3 * 24 * 60 * 60 * 1000;
        await updateDoc(doc(database, 'users', signaleurId), {
          suspendu: true, suspendJusqu, nbAvertissements,
        });
      } else {
        await updateDoc(doc(database, 'users', signaleurId), { nbAvertissements });
      }
    } catch (e) { console.warn("traiterAvertissement:", e); }
  }, []);

  // ── Feedback OUI ──────────────────────────────────────────────────────────
  const handleFeedback = async (aReussi) => {
    setShowFeedbackModal(false);
    if (aReussi) {
      try {
        await addDoc(collection(database, 'parkingFeedback'), {
          success: true, timestamp: Date.now(),
          userId:  getAuth().currentUser?.uid || 'anonymous',
          spotId:  spotIdSelectionne || null,
        });
        if (spotIdSelectionne) {
          try { await deleteDoc(doc(database, 'freeSpots', spotIdSelectionne)); } catch {}
        }
      } catch (e) { console.warn("feedback:", e); }
      alert('Super ! Bonne journée 🎉');
      setPlaceSelectionnee(null); setSpotIdSelectionne(null);
      setSpotSignaleurId(null);   setPlaceEstBorne(false);
    } else {
      setShowRaisonModal(true);
    }
  };

  // ── Feedback NON + raison ─────────────────────────────────────────────────
  const handleRaisonNon = async (raison) => {
    setShowRaisonModal(false);
    try {
      await addDoc(collection(database, 'parkingFeedback'), {
        success: false, raison, timestamp: Date.now(),
        userId:  getAuth().currentUser?.uid || 'anonymous',
        spotId:  spotIdSelectionne || null,
      });
      if (spotIdSelectionne) {
        try { await deleteDoc(doc(database, 'freeSpots', spotIdSelectionne)); } catch {}
      }
      if ((raison === 'faux_signalement' || raison === 'mauvais_type') && spotSignaleurId) {
        await traiterAvertissement(spotSignaleurId, spotIdSelectionne, raison);
      }
    } catch (e) { console.warn("raison:", e); }
    const messages = {
      place_occupee:    'Merci pour l\'info ! La place a été retirée.',
      mauvais_type:     'Merci ! Le signaleur a été averti.',
      faux_signalement: 'Merci ! Le signaleur a été averti.',
    };
    alert(messages[raison] || 'Merci pour votre retour !');
    setPlaceSelectionnee(null); setSpotIdSelectionne(null);
    setSpotSignaleurId(null);   setPlaceEstBorne(false);
  };

  // ── Cleanup timers ────────────────────────────────────────────────────────
  useEffect(() => {
  const feedbackTimer = feedbackTimerRef.current;
  const borneTimer    = borneTimerRef.current;
  return () => {
    if (feedbackTimer) clearTimeout(feedbackTimer);
    if (borneTimer)    clearTimeout(borneTimer);
  };
}, []);

  // ── Helper géolocalisation ────────────────────────────────────────────────
  const demanderGeolocalisation = useCallback((onSuccess, onErreur) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      onSuccess,
      (err) => { onErreur && onErreur(err); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // ── chargerSignalementsJour ───────────────────────────────────────────────
  const chargerSignalementsJour = useCallback(async (userId) => {
    try {
      const local = getCompteurLocal(userId);
      setSignalementsAujourdhui(local.count);
      setDernierSignalement(local.dernierSignalement);
      const debutJour = new Date();
      debutJour.setHours(0, 0, 0, 0);
      const q = query(
        collection(database, 'freeSpots'),
        where('reportedBy', '==', userId),
        where('timestamp',  '>=', debutJour.getTime())
      );
      const snap  = await getDocs(q);
      const count = snap.size;
      let dernierSig = null;
      if (count > 0) {
        const spots = snap.docs.map(d => d.data()).sort((a, b) => b.timestamp - a.timestamp);
        dernierSig = { timestamp: spots[0].timestamp, lat: spots[0].latitude, lng: spots[0].longitude };
      }
      setSignalementsAujourdhui(count);
      setDernierSignalement(dernierSig);
      setCompteurLocal(userId, count, dernierSig);
    } catch (err) { console.warn("signalements:", err); }
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setIsAuthenticated(!!user);
      if (user) await chargerSignalementsJour(user.uid);
      else { setSignalementsAujourdhui(0); setDernierSignalement(null); }
    });
    return () => unsubscribe();
  }, [chargerSignalementsJour]);

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) chargerSignalementsJour(user.uid);
  }, [chargerSignalementsJour]);

  const verifierContraintes = (newLat, newLng) => {
    if (!estDansZone(newLat, newLng))
      return { ok: false, message: `📍 Vous devez être dans un rayon de ${HOPITAL.rayon}m autour de l'${HOPITAL.nom} pour signaler une place.` };
    if (signalementsAujourdhui >= DAILY_LIMIT)
      return { ok: false, message: `Limite de ${DAILY_LIMIT} signalements par jour atteinte.` };
    if (dernierSignalement) {
      const delaiEcoule = Date.now() - dernierSignalement.timestamp;
      if (delaiEcoule < MIN_DELAY_MS) {
        const restantSec = Math.ceil((MIN_DELAY_MS - delaiEcoule) / 1000);
        return { ok: false, message: `Attendez encore ${Math.floor(restantSec/60)}min ${restantSec%60}s.` };
      }
      if (calculateDistanceMeters(dernierSignalement.lat, dernierSignalement.lng, newLat, newLng) < MIN_DISTANCE_M)
        return { ok: false, message: `Déplacez-vous d'au moins ${MIN_DISTANCE_M}m.` };
    }
    return { ok: true, message: "" };
  };

  // ── Vérifie suspension ────────────────────────────────────────────────────
  const verifierSuspension = async () => {
    const user = getAuth().currentUser;
    if (!user) return false;
    try {
      const userSnap = await getDoc(doc(database, 'users', user.uid));
      if (!userSnap.exists()) return false;
      const data = userSnap.data();
      if (data.suspendu && data.suspendJusqu > Date.now()) {
        const jours = Math.ceil((data.suspendJusqu - Date.now()) / (1000 * 60 * 60 * 24));
        alert(`⚠️ Votre accès au signalement est suspendu pendant encore ${jours} jour(s) suite à des signalements incorrects.`);
        return true;
      }
      if (data.suspendu && data.suspendJusqu <= Date.now()) {
        await updateDoc(doc(database, 'users', user.uid), { suspendu: false, suspendJusqu: null });
      }
      return false;
    } catch { return false; }
  };

  const calculateAndDisplayRoute = useCallback(async (originCoords, destCoords) => {
    const currentMap = mapRef.current;
    if (!currentMap || !window.H) return;
    try {
      const platform = new window.H.service.Platform({ apikey: process.env.REACT_APP_HERE_API_KEY });
      const router   = platform.getRoutingService(null, 8);
      router.calculateRoute(
        { routingMode: 'fast', transportMode: 'car', origin: `${originCoords.lat},${originCoords.lng}`, destination: `${destCoords.lat},${destCoords.lng}`, return: 'polyline,summary' },
        (result) => {
          if (!result.routes?.length) return;
          try {
            if (routeLineRef.current)          currentMap.removeObject(routeLineRef.current);
            if (spotsMarkersGroup.current)     currentMap.removeObject(spotsMarkersGroup.current);
            if (userMarkerRef.current)       { currentMap.removeObject(userMarkerRef.current); setUserMarker(null); }
            if (routeMarkersRef.current.start) currentMap.removeObject(routeMarkersRef.current.start);
            if (routeMarkersRef.current.end)   currentMap.removeObject(routeMarkersRef.current.end);
            const section      = result.routes[0].sections[0];
            const linestring   = window.H.geo.LineString.fromFlexiblePolyline(section.polyline);
            const newRouteLine = new window.H.map.Polyline(linestring, { style: { strokeColor: '#0d6efd', lineWidth: 5 } });
            currentMap.addObject(newRouteLine); setRouteLine(newRouteLine);
            const sM = new window.H.map.Marker({ lat: originCoords.lat, lng: originCoords.lng }, { icon: createPinMarker('#27ae60') });
            const eM = new window.H.map.Marker({ lat: destCoords.lat,   lng: destCoords.lng   }, { icon: createPinMarker('#e74c3c') });
            currentMap.addObject(sM); currentMap.addObject(eM);
            setRouteMarkers({ start: sM, end: eM });
            currentMap.getViewModel().setLookAtData({ bounds: newRouteLine.getBoundingBox() });
            if (section.summary?.duration) setRouteDuration(section.summary.duration);
            setHasRoute(true);
          } catch (e) { console.error("route display:", e); }
        },
        (e) => console.error("calculateRoute:", e)
      );
    } catch (e) { console.error("calculateAndDisplayRoute:", e); }
  }, []);

  const handleResetRoute = useCallback(() => {
    const currentMap = mapRef.current;
    if (currentMap) {
      try {
        if (routeLineRef.current)          { currentMap.removeObject(routeLineRef.current); setRouteLine(null); }
        if (routeMarkersRef.current.start) { currentMap.removeObject(routeMarkersRef.current.start); }
        if (routeMarkersRef.current.end)   { currentMap.removeObject(routeMarkersRef.current.end); }
        setRouteMarkers({ start: null, end: null });
        currentMap.setCenter({ lat: HOPITAL.lat, lng: HOPITAL.lng });
        currentMap.setZoom(16);
      } catch (e) { console.warn("handleResetRoute:", e); }
    }
    setStart(''); setStartCoords(null);
    setEnd('');   setEndCoords(null);
    setRouteDuration(null);
    setHasRoute(false);
  }, []);

  const displayAvailableSpots = useCallback((spots, userCoords) => {
    const currentMap = mapRef.current;
    if (!currentMap || !window.H) return;
    try {
      if (spotsMarkersGroup.current) currentMap.removeObject(spotsMarkersGroup.current);
      spotsMarkersGroup.current = new window.H.map.Group();
      spots.forEach(spot => {
        const { color, label } = getSpotStatus(spot.timestamp);
        if (!color) return;
        const ageMin  = Math.floor((Date.now() - spot.timestamp) / 60000);
        const distStr = userCoords
          ? distanceApproximative(calculateDistanceMeters(userCoords.lat, userCoords.lng, spot.latitude, spot.longitude))
          : '—';
        const estBorne  = spot.typePlace === 'borne_electrique';
        const pinColor  = estBorne ? '#12f3e0' : color;

        const labelType = (() => {
          const ref = spot.referenceEmplacement ? ` — Emplacement ${spot.referenceEmplacement}` : '';
          switch (spot.typePlace) {
            case 'exterieur':        return '🌤️ Extérieur';
            case 'personnel':        return '🏥 Parking personnel';
            case 'sous_sol':         return `⬇️ Sous-sol Niveau ${spot.niveauPlace || ''}${ref}`;
            case 'etage':            return `⬆️ Étage ${spot.niveauPlace || ''}${ref}`;
            case 'borne_electrique': return '⚡ Borne de recharge';
            default:                 return '';
          }
        })();

        const marker = new window.H.map.Marker(
          { lat: spot.latitude, lng: spot.longitude },
          { icon: createPinMarker(pinColor) }
        );

        marker.addEventListener('tap', () => {
          if (uiRef.current) uiRef.current.getBubbles().forEach(b => uiRef.current.removeBubble(b));
          const el = document.createElement('div');
          el.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:6px;min-width:180px;';
          el.innerHTML = `
            <div style="font-weight:bold;font-size:1rem;">${estBorne ? '⚡ Borne de recharge' : 'Place de parking'}</div>
            ${labelType && !estBorne ? `<div style="font-size:0.82rem;background:#e8f8f5;padding:4px 8px;border-radius:6px;color:#12f3e0;font-weight:600;">${labelType}</div>` : ''}
            <div style="font-size:0.85rem;color:#555;">🕐 Il y a <strong>${ageMin} min</strong> — 📍 <strong>${distStr}</strong></div>
            <div style="display:flex;gap:6px;align-items:center;">
              <span style="width:12px;height:12px;border-radius:50%;background:${color};border:1px solid #333;display:inline-block;"></span>
              <span style="font-size:0.85rem;">${label} (< ${ageMin<5?5:ageMin<10?10:15} min)</span>
            </div>
            <button id="btn-nav" style="background:linear-gradient(135deg,${estBorne?'#07a4a9,#22b8e6':'#07a993,#0d6efd'});color:#fff;border:none;padding:10px;border-radius:8px;cursor:pointer;font-weight:600;width:100%;font-size:0.9rem;">
              ${estBorne ? '⚡ Naviguer vers la borne' : '📡 Naviguer vers cette place'}
            </button>
          `;
          const bubble = new window.H.ui.InfoBubble(
            { lat: spot.latitude, lng: spot.longitude },
            { content: el }
          );
          uiRef.current.addBubble(bubble);
          setTimeout(() => {
            const bNav = el.querySelector('#btn-nav');
            if (bNav) bNav.onclick = () => {
              uiRef.current.removeBubble(bubble);
              setPlaceSelectionnee({ lat: spot.latitude, lng: spot.longitude });
              setSpotIdSelectionne(spot.id);
              setSpotSignaleurId(spot.reportedBy || null);
              setPlaceEstBorne(estBorne);
              setShowNavModal(true);
            };
          }, 0);
        });
        spotsMarkersGroup.current.addObject(marker);
      });
      currentMap.addObject(spotsMarkersGroup.current);
    } catch (e) { console.error("displayAvailableSpots:", e); }
  }, []);

  useEffect(() => {
    if (!map) return;
    const unsub = onSnapshot(query(collection(database,'freeSpots')), async (snap) => {
      const spots = [], dels = [];
      snap.forEach(d => {
        const data = { id: d.id, ...d.data() };
        if (getSpotStatus(data.timestamp).expired) {
          dels.push(deleteDoc(doc(database,'freeSpots',d.id)));
        } else if (estDansZone(data.latitude, data.longitude)) {
          spots.push(data);
        }
      });
      if (dels.length) await Promise.all(dels);
      setAvailableSpots(spots);
    });
    return () => unsub();
  }, [map]);

  useEffect(() => {
    if (!map || !availableSpots.length) return;
    const iv = setInterval(() => {
      if (userPositionRef.current) displayAvailableSpots(availableSpots, userPositionRef.current);
    }, 30000);
    return () => clearInterval(iv);
  }, [map, availableSpots, displayAvailableSpots]);

  useEffect(() => {
    if (mapInitialized.current) return;
    let attempts = 0;
    const tryInit = () => {
      if (window.H?.service?.Platform && window.H.Map && mapContainerRef.current) { initializeMap(); return; }
      if (attempts++ < 50) setTimeout(tryInit, 100);
    };
    tryInit();
    return () => { if (mapRef.current) { try { mapRef.current.dispose(); } catch {} } };
    // eslint-disable-next-line
  }, []);

  const initializeMap = () => {
    if (mapInitialized.current || mapRef.current) return;
    try {
      const apiKey = process.env.REACT_APP_HERE_API_KEY;
      if (!apiKey || !mapContainerRef.current) return;
      const platform      = new window.H.service.Platform({ apikey: apiKey });
      const defaultLayers = platform.createDefaultLayers();
      const mapInstance   = new window.H.Map(
        mapContainerRef.current, defaultLayers.vector.normal.map,
        { center: { lat: HOPITAL.lat, lng: HOPITAL.lng }, zoom: 16, pixelRatio: window.devicePixelRatio || 1 }
      );
      if (window.H.mapevents) new window.H.mapevents.Behavior(new window.H.mapevents.MapEvents(mapInstance));
      const cercle = new window.H.map.Circle(
        { lat: HOPITAL.lat, lng: HOPITAL.lng }, HOPITAL.rayon,
        { style: { strokeColor: 'rgba(231,76,60,0.8)', lineWidth: 2, fillColor: 'rgba(231,76,60,0.06)' } }
      );
      mapInstance.addObject(cercle);
      const svgHopital = `<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="18" fill="#e74c3c" stroke="white" stroke-width="3"/>
        <text x="20" y="26" text-anchor="middle" font-size="18" fill="white">🏥</text>
      </svg>`;
      mapInstance.addObject(new window.H.map.Marker(
        { lat: HOPITAL.lat, lng: HOPITAL.lng },
        { icon: new window.H.map.Icon(`data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgHopital)}`, { size: { w: 40, h: 40 }, anchor: { x: 20, y: 20 } }) }
      ));
      mapInstance.addEventListener('mapviewchangeend', () => {
        const center = mapInstance.getCenter();
        if (calculateDistanceMeters(center.lat, center.lng, HOPITAL.lat, HOPITAL.lng) > 1000)
          mapInstance.setCenter({ lat: HOPITAL.lat, lng: HOPITAL.lng }, true);
      });
      uiRef.current = window.H.ui.UI.createDefault(mapInstance, defaultLayers);
      setMap(mapInstance);
      mapInitialized.current = true;
    } catch (e) { console.error('initializeMap:', e); }
  };

  const placeUserMarker = (coordinates) => {
    const currentMap = mapRef.current;
    if (!currentMap || !window.H) return;
    try {
      if (userMarkerRef.current)         currentMap.removeObject(userMarkerRef.current);
      if (spotsMarkersGroup.current)     currentMap.removeObject(spotsMarkersGroup.current);
      if (routeLineRef.current)        { currentMap.removeObject(routeLineRef.current); setRouteLine(null); }
      if (routeMarkersRef.current.start) currentMap.removeObject(routeMarkersRef.current.start);
      if (routeMarkersRef.current.end)   currentMap.removeObject(routeMarkersRef.current.end);
      const newMarker = new window.H.map.Marker(
        { lat: coordinates.lat, lng: coordinates.lng },
        { icon: createPinMarker('#007bff') }
      );
      currentMap.addObject(newMarker); setUserMarker(newMarker);
      currentMap.setCenter({ lat: coordinates.lat, lng: coordinates.lng });
      currentMap.setZoom(17);
      getAddressFromCoordinates(coordinates);
    } catch (e) { console.error("placeUserMarker:", e); }
  };

  const getAddressFromCoordinates = (coordinates) => {
    const apiKey = process.env.REACT_APP_HERE_API_KEY;
    if (!window.H || !apiKey) return;
    try {
      new window.H.service.Platform({ apikey: apiKey }).getSearchService().reverseGeocode(
        { at: `${coordinates.lat},${coordinates.lng}` },
        (r) => {
          if (r.items?.length) {
            const a = r.items[0].address;
            setUserAddress(`${a.street||''} ${a.houseNumber||''}, ${a.postalCode||''} ${a.city||''}`);
          }
        },
        (e) => console.warn('reverseGeocode:', e)
      );
    } catch (e) { console.warn("getAddressFromCoordinates:", e); }
  };

  const getAddressFromCoordinatesAsync = (coordinates) => {
    const apiKey = process.env.REACT_APP_HERE_API_KEY;
    if (!window.H || !apiKey) return Promise.resolve('');
    return new Promise(resolve => {
      try {
        new window.H.service.Platform({ apikey: apiKey }).getSearchService().reverseGeocode(
          { at: `${coordinates.lat},${coordinates.lng}` },
          (r) => {
            if (r.items?.length) { const a = r.items[0].address; resolve(`${a.street||''} ${a.houseNumber||''}, ${a.postalCode||''} ${a.city||''}`); }
            else resolve('');
          },
          () => resolve('')
        );
      } catch { resolve(''); }
    });
  };

  const handleGeolocateStart = () => {
    demanderGeolocalisation(
      async (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setStart(await getAddressFromCoordinatesAsync(coords));
        setStartCoords(coords);
      },
      null
    );
  };

  const handleSignalFreeSpot = () => {
    if (!isAuthenticated) { alert('Vous devez être connecté'); return; }
    verifierSuspension().then((estSuspendu) => {
      if (estSuspendu) return;
      demanderGeolocalisation(
        async (pos) => {
          const lat = pos.coords.latitude, lng = pos.coords.longitude;
          const { ok, message } = verifierContraintes(lat, lng);
          if (!ok) { setSignalementMessage(message); return; }
          setSignalementMessage(""); setZoneMessage("");
          setCoordsEnAttente({ lat, lng });
          setTypePlace('');
          setNiveauPlace('');
          setReferenceEmplacement('');
          setShowTypeModal(true);
        },
        null
      );
    });
  };

  const saveFreeSpot = async (coordinates, type, niveau, reference) => {
    const user = getAuth().currentUser;
    if (!user) { alert('Vous devez être connecté'); return; }
    try {
      const spotData = {
        latitude:             coordinates.lat,
        longitude:            coordinates.lng,
        address:              userAddress,
        timestamp:            Date.now(),
        isAvailable:          true,
        reportedBy:           user.uid,
        typePlace:            type      || 'exterieur',
        niveauPlace:          niveau    || null,
        referenceEmplacement: reference || null,
      };

      // ── Place active (supprimée après 15 min) ──────────────────────────
      await addDoc(collection(database,'freeSpots'), spotData);

      // ── Historique permanent pour le dashboard ─────────────────────────
      await addDoc(collection(database,'historique_signalements'), {
        ...spotData,
        siteId: 'mignot',
      });

      await updateDoc(doc(database,'users',user.uid), { points: increment(5) });
      const n          = signalementsAujourdhui + 1;
      const dernierSig = { timestamp: Date.now(), lat: coordinates.lat, lng: coordinates.lng };
      setSignalementsAujourdhui(n);
      setDernierSignalement(dernierSig);
      setCompteurLocal(user.uid, n, dernierSig);
      setSignalementMessage(`Place signalée ! +5 points. (${n}/${DAILY_LIMIT} aujourd'hui)`);
    } catch (e) { console.error('saveFreeSpot:', e); alert("Erreur lors de l'enregistrement"); }
  };

  const handleShowFreeSpots = () => {
    if (!isAuthenticated) { alert('Vous devez être connecté'); return; }
    demanderGeolocalisation(
      (pos) => {
        const userCoords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setZoneMessage(""); setUserPosition(userCoords);
        const currentMap = mapRef.current;
        if (!currentMap) return;
        currentMap.setCenter({ lat: HOPITAL.lat, lng: HOPITAL.lng });
        currentMap.setZoom(16);
        if (userMarkerRef.current)         { currentMap.removeObject(userMarkerRef.current); setUserMarker(null); }
        if (routeLineRef.current)          { currentMap.removeObject(routeLineRef.current);  setRouteLine(null);  }
        if (routeMarkersRef.current.start)   currentMap.removeObject(routeMarkersRef.current.start);
        if (routeMarkersRef.current.end)     currentMap.removeObject(routeMarkersRef.current.end);
        displayAvailableSpots(availableSpots, userCoords);
        if (availableSpots.length === 0) {
          setZoneMessage("😕 Aucune place disponible pour le moment. Revenez un peu plus tard !");
        }
      },
      null
    );
  };

  // ── handleRoute — destination libre (France entière) ─────────────────────
  const handleRoute = async (e) => {
    e.preventDefault();
    if (!mapRef.current || !window.H) return;
    if (!end && !endCoords) { alert("Veuillez saisir une adresse d'arrivée."); return; }
    // ← Plus de restriction de zone pour la destination
    try {
      const o = startCoords || await geocodeWithCache(start);
      // Utilise geocodeLibre pour la destination (France entière)
      const d = endCoords   || await geocodeLibre(end);
      if (!startCoords) setStartCoords(o);
      if (!endCoords)   setEndCoords(d);
      await calculateAndDisplayRoute(o, d);
    } catch (err) { alert(err.message); }
  };

  const messageColor = signalementMessage.startsWith("Place signalée") ? "#198754" : "#dc3545";

  return (
    <div style={{ padding: '0 8px' }}>

      {/* ── Modal type de place ───────────────────────────────────────────── */}
      {showTypeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px 20px', maxWidth: '340px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontSize: '2rem', textAlign: 'center', marginBottom: '12px' }}>🔓</div>
            <h5 style={{ fontWeight: 700, textAlign: 'center', marginBottom: '16px' }}>Précisez votre place</h5>
            <p style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: '8px' }}>Type de place :</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {[
                { val: 'exterieur',        label: '🌤️ Extérieur / Surface'          },
                { val: 'personnel',        label: '🏥 Parking réservé au personnel'  },
                { val: 'sous_sol',         label: '⬇️ Sous-sol'                     },
                { val: 'etage',            label: '⬆️ Parking en étage'             },
                { val: 'borne_electrique', label: '⚡ Borne de recharge électrique'  },
              ].map(({ val, label }) => (
                <button key={val} type="button"
                  onClick={() => { setTypePlace(val); setNiveauPlace(''); }}
                  style={{
                    padding: '12px', borderRadius: '10px', textAlign: 'left',
                    border: typePlace === val ? '2px solid #07a993' : '1px solid #dee2e6',
                    background: typePlace === val ? '#e8f8f5' : '#fff',
                    fontWeight: typePlace === val ? 700 : 400,
                    color: typePlace === val ? '#07a993' : '#333',
                    cursor: 'pointer', fontSize: '0.88rem',
                  }}>
                  {label}
                </button>
              ))}
            </div>
            {(typePlace === 'sous_sol' || typePlace === 'etage') && (
              <>
                <p style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: '8px' }}>
                  {typePlace === 'sous_sol' ? 'Niveau :' : 'Étage :'}
                </p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  {(typePlace === 'sous_sol'
                    ? ['-1', '-2', '-3', '-4']
                    : ['1', '2', '3', '4', '5']
                  ).map((n) => (
                    <button key={n} type="button"
                      onClick={() => setNiveauPlace(n)}
                      style={{
                        padding: '10px 14px', borderRadius: '8px',
                        border: niveauPlace === n ? '2px solid #07a993' : '1px solid #dee2e6',
                        background: niveauPlace === n ? '#e8f8f5' : '#fff',
                        fontWeight: niveauPlace === n ? 700 : 400,
                        color: niveauPlace === n ? '#07a993' : '#333',
                        cursor: 'pointer', fontSize: '0.88rem',
                      }}>
                      {typePlace === 'sous_sol' ? `Niveau ${n}` : `Étage ${n}`}
                    </button>
                  ))}
                </div>

                {/* ── Référence d'emplacement — visible après sélection du niveau ── */}
                {niveauPlace && (
                  <>
                    <p style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: '8px' }}>
                      Référence de l'emplacement{' '}
                      <span style={{ fontWeight: 400, color: '#6c757d' }}>(optionnel)</span> :
                    </p>
                    <input
                      type="text"
                      placeholder="ex : A12, 42, B-07..."
                      value={referenceEmplacement}
                      onChange={(e) => setReferenceEmplacement(e.target.value.toUpperCase())}
                      maxLength={10}
                      style={{
                        width: '100%', padding: '10px 12px',
                        borderRadius: '8px',
                        border: referenceEmplacement ? '2px solid #07a993' : '1px solid #dee2e6',
                        fontSize: '0.95rem', fontWeight: 600,
                        letterSpacing: '2px', textAlign: 'center',
                        marginBottom: '16px', outline: 'none',
                        background: referenceEmplacement ? '#e8f8f5' : '#fff',
                        color: '#07a993',
                      }}
                    />
                  </>
                )}
              </>
            )}
            {typePlace === 'borne_electrique' && (
              <div style={{ background: '#e8f8f5', border: '1px solid #07a993', borderRadius: '10px', padding: '10px 12px', marginBottom: '16px', fontSize: '0.82rem', color: '#07a993' }}>
                ⚡ L'utilisateur qui se garera sur cette borne recevra une notification après 4h pour la libérer.
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                disabled={!typePlace || ((typePlace === 'sous_sol' || typePlace === 'etage') && !niveauPlace)}
                onClick={async () => {
                  setShowTypeModal(false);
                  if (!coordsEnAttente) return;
                  placeUserMarker(coordsEnAttente);
                  setUserPosition(coordsEnAttente);
                  await saveFreeSpot(coordsEnAttente, typePlace, niveauPlace, referenceEmplacement);
                  setCoordsEnAttente(null);
                  setReferenceEmplacement('');
                }}
                style={{
                  flex: 2,
                  padding: '12px',
                  borderRadius: '10px',
                  background: typePlace && ((typePlace !== 'sous_sol' && typePlace !== 'etage') || niveauPlace)
                    ? '#07a993'
                    : '#ccc',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: typePlace ? 'pointer' : 'not-allowed',
                  border: 'none',
                  fontSize: '0.95rem',
                }}
              >
                🔓 Libérer ma place
              </button>
               {/* 👇 NOUVEAU BOUTON ANNULER */}
              <button
                type="button"
                onClick={() => {
                  setShowTypeModal(false);
                  setCoordsEnAttente(null);
                  setTypePlace('');
                  setNiveauPlace('');
                  setReferenceEmplacement('');
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  background: '#f8f9fa',
                  color: '#6c757d',
                  border: '1px solid #dee2e6',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                }}
              >
                ❌ Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal navigation Waze / Google Maps ──────────────────────────── */}
      {showNavModal && placeSelectionnee && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px 20px', maxWidth: '320px', width: '100%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>{placeEstBorne ? '⚡' : '🅿️'}</div>
            <h5 style={{ fontWeight: 700, marginBottom: '8px' }}>
              {placeEstBorne ? 'Naviguer vers la borne' : 'Naviguer vers la place'}
            </h5>
            <p style={{ color: '#555', fontSize: '0.85rem', marginBottom: '6px', lineHeight: 1.6 }}>
              {placeEstBorne
                ? 'SpotyPark vous notifiera après 4h pour libérer la borne.'
                : 'SpotyPark vous enverra une notification pour savoir si vous avez trouvé votre place.'}
            </p>
            <p style={{ color: '#6c757d', fontSize: '0.82rem', marginBottom: '20px' }}>
              Choisissez votre application de navigation :
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button onClick={() => ouvrirWaze(placeSelectionnee, userPositionRef.current, placeEstBorne, true)}
                style={{ width: '100%', padding: '14px', borderRadius: '10px', background: '#33ccff', color: '#000', border: 'none', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}>
                <img src={waze} alt='logo waze' width={30} /> Waze
              </button>
              <button onClick={() => ouvrirGoogleMaps(placeSelectionnee, userPositionRef.current, placeEstBorne, true)}
                style={{ width: '100%', padding: '14px', borderRadius: '10px', background: '#4285f4', color: '#fff', border: 'none', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}>
                <img src={google_maps} alt="logo google maps" width={30} /> Google Maps
              </button>
              <button onClick={() => setShowNavModal(false)}
                style={{ width: '100%', padding: '10px', borderRadius: '10px', background: '#fff', color: '#6c757d', border: '1px solid #dee2e6', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal feedback ────────────────────────────────────────────────── */}
      {showFeedbackModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px 20px', maxWidth: '320px', width: '100%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🅿️</div>
            <h5 style={{ fontWeight: 700, marginBottom: '8px' }}>Avez-vous trouvé votre place ?</h5>
            <p style={{ color: '#6c757d', fontSize: '0.85rem', marginBottom: '20px' }}>
              Votre retour nous aide à améliorer SpotyPark !
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => handleFeedback(true)}
                style={{ flex: 1, padding: '14px', borderRadius: '10px', background: '#27ae60', color: '#fff', border: 'none', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}>
                ✅ Oui !
              </button>
              <button onClick={() => handleFeedback(false)}
                style={{ flex: 1, padding: '14px', borderRadius: '10px', background: '#e74c3c', color: '#fff', border: 'none', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}>
                ❌ Non
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal raison — réponse NON ────────────────────────────────────── */}
      {showRaisonModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px 20px', maxWidth: '340px', width: '100%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🤔</div>
            <h5 style={{ fontWeight: 700, marginBottom: '8px' }}>
              Pourquoi n'avez-vous pas pu vous garer ?
            </h5>
            <p style={{ color: '#6c757d', fontSize: '0.85rem', marginBottom: '20px' }}>
              Votre retour nous aide à améliorer la qualité des signalements.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button onClick={() => handleRaisonNon('place_occupee')}
                style={{ width: '100%', padding: '14px', borderRadius: '10px', background: '#f8f9fa', color: '#333', border: '1px solid #dee2e6', fontWeight: 600, fontSize: '0.92rem', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.5rem' }}>🚗</span>
                <span>Place déjà occupée</span>
              </button>
              <button onClick={() => handleRaisonNon('mauvais_type')}
                style={{ width: '100%', padding: '14px', borderRadius: '10px', background: '#e8f0fe', color: '#0d6efd', border: '1px solid #0d6efd', fontWeight: 600, fontSize: '0.92rem', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.5rem' }}>📍</span>
                <span>La place ne correspond pas à ce qui était indiqué</span>
              </button>
              <button onClick={() => handleRaisonNon('faux_signalement')}
                style={{ width: '100%', padding: '14px', borderRadius: '10px', background: '#fff3cd', color: '#856404', border: '1px solid #ffc107', fontWeight: 600, fontSize: '0.92rem', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                <span>Stationnement non autorisé</span>
              </button>
              <button onClick={() => {
                setShowRaisonModal(false);
                setPlaceSelectionnee(null);
                setSpotIdSelectionne(null);
                setSpotSignaleurId(null);
              }}
                style={{ width: '100%', padding: '10px', borderRadius: '10px', background: '#fff', color: '#6c757d', border: '1px solid #dee2e6', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
                Ignorer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bannière zone POC ─────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #07a993 0%, #0d6efd 100%)',
        color: '#fff', padding: '8px 14px', borderRadius: '8px',
        marginBottom: '10px', fontSize: '0.82rem', fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: '8px',
      }}>
        🏥 Zone active : <strong>{HOPITAL.nom}</strong> — rayon {HOPITAL.rayon}m
      </div>

      {zoneMessage && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '8px 12px', marginBottom: '8px', fontSize: '0.82rem', color: '#856404' }}>
          {zoneMessage}
        </div>
      )}

      {/* ── Boutons actions ───────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
        <Button onClick={handleSignalFreeSpot} className='signaler'
          style={{ fontWeight: 600, flex: '1 1 140px', minHeight: '44px', fontSize: '0.88rem' }}
          disabled={!isAuthenticated || signalementsAujourdhui >= DAILY_LIMIT}>
          🔓 Libérer ma place
        </Button>
        <Button onClick={handleShowFreeSpots} className='visualisation-place'
          style={{ borderColor: '#8e44ad', fontWeight: 600, flex: '1 1 120px', minHeight: '44px', fontSize: '0.88rem' }}
          disabled={!isAuthenticated}>
          🅿️ Trouver une Place
        </Button>
      </div>

      {!isAuthenticated && (
        <div style={{ fontSize: '0.82rem', color: '#e74c3c', fontWeight: 600, marginBottom: '8px', padding: '6px 10px', background: '#fce8e8', borderRadius: '6px' }}>
          Connectez-vous pour utiliser ces fonctionnalités
        </div>
      )}

      {isAuthenticated && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: signalementsAujourdhui >= DAILY_LIMIT ? '#dc3545' : '#6c757d' }}>
            Signalements : {signalementsAujourdhui}/{DAILY_LIMIT}
          </span>
          {signalementMessage && (
            <span style={{ fontSize: '0.8rem', fontWeight: 500, color: messageColor }}>{signalementMessage}</span>
          )}
        </div>
      )}

      {/* ── Layout principal ──────────────────────────────────────────────── */}
      <div className="row g-2">
        <div className="col-12 col-md-4">
          <form className="d-flex flex-column gap-2 mb-3" onSubmit={handleRoute}>

            {/* Adresse de départ — zone restreinte */}
            <AddressInput
              value={start}
              onChange={(val, coords) => { setStart(val); setStartCoords(coords); }}
              placeholder="Votre adresse de départ"
              showGeolocateBtn={true}
              onGeolocate={handleGeolocateStart}
              modeLibre={false}
            />

            {/* Adresse d'arrivée — France entière ← NOUVEAU */}
            <AddressInput
              value={end}
              onChange={(val, coords) => { setEnd(val); setEndCoords(coords); }}
              placeholder="Votre destination"
              showGeolocateBtn={false}
              modeLibre={true}
            />

            <Button type="submit" className='btn-itineraire'
              style={{ borderColor: '#66d8ae', fontWeight: 600, minHeight: '44px', fontSize: '0.88rem' }}><LuMilestone size={24} className='me-1' />
              Itinéraire
            </Button>

            {hasRoute && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {routeDuration !== null && (
                  <div style={{ background: '#e8f8f5', border: '1px solid #07a993', borderRadius: '6px', padding: '8px 12px', fontSize: '0.85rem', fontWeight: 600, color: '#07a993', whiteSpace: 'nowrap' }}>
                    ⏱️ {formatDuration(routeDuration)}
                  </div>
                )}
                <button type="button"
                  onClick={() => handleNaviguerFormulaire(ouvrirWaze)}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', background: '#33ccff', color: '#000', border: 'none', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                  <img src={waze} alt='logo waze' width={30} />
                </button>
                <button type="button"
                  onClick={() => handleNaviguerFormulaire(ouvrirGoogleMaps)}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', background: '#4285f4', color: '#fff', border: 'none', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                  <img src={google_maps} alt="logo google maps" width={30} />
                </button>
                <button type="button" onClick={handleResetRoute}
                  style={{ padding: '10px', borderRadius: '8px', border: '1px solid #dc3545', background: '#fff', color: '#dc3545', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
                  ✕
                </button>
              </div>
            )}
          </form>

          {/* Légende */}
          <div style={{ padding: '10px 12px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6', marginBottom: '8px' }}>
            <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '0.82rem' }}>Places libérées il y a moins de :</div>
            {[{ color: '#27ae60', label: '5 min' }, { color: '#f39c12', label: '10 min' }, { color: '#e74c3c', label: '15 min' }].map(({ color, label }) => (
              <div key={color} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: color, border: '1px solid #333', flexShrink: 0 }} />
                <span style={{ fontSize: '0.78rem' }}>{label}</span>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #dee2e6' }}>
              <svg width="14" height="14" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <path d="M16 2C9.37258 2 4 7.37258 4 14C4 23.5 16 30 16 30C16 30 28 23.5 28 14C28 7.37258 22.6274 2 16 2Z" fill="#12f3e8" stroke="#333" strokeWidth="2"/>
                <circle cx="16" cy="14" r="5" fill="white" stroke="#333" strokeWidth="2"/>
              </svg>
              <span style={{ fontSize: '0.78rem' }}>⚡ Borne de recharge électrique</span>
            </div>
          </div>

          <div style={{ fontSize: '0.78rem', color: '#6c757d', textAlign: 'center' }}>
            <strong>{availableSpots.length}</strong> place{availableSpots.length > 1 ? 's' : ''} disponible{availableSpots.length > 1 ? 's' : ''} dans la zone
          </div>
        </div>

        {/* Carte */}
        <div className="col-12 col-md-8">
          <div ref={mapContainerRef} style={{ width: '100%', height: 'clamp(280px, 55vw, 520px)', background: '#e8e8e8', borderRadius: '8px', overflow: 'hidden' }} />
          {userAddress && (
            <div style={{ marginTop: '6px', fontSize: '0.78rem', color: '#6c757d', padding: '0 4px' }}>
              📍 {userAddress}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HereMapComponent;